/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useState, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import { ClientStyleContext } from "./chakra/context";
import { createEmotionCache, defaultCache } from "./chakra/createEmotionCache";
import { CacheProvider } from "@emotion/react";
import type { i18n } from "i18next";
import { createInstance } from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { getInitialNamespaces } from "remix-i18next";
import { i18nConfig } from "i18n.config";
import posthog from "posthog-js";

function PosthogInit() {
  useEffect(() => {
    posthog.init("phc_SFVbXjouWTzrkKMESCQMNF2tGGhL9v66JbwxBeDlWPo", {
      api_host: "https://eu.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      persistence: "sessionStorage",
    });
  }, []);

  return null;
}

interface ClientCacheProviderProps {
  children: React.ReactNode;
}

function ClientCacheProvider({ children }: ClientCacheProviderProps) {
  const [cache, setCache] = useState(defaultCache);

  function reset() {
    setCache(createEmotionCache());
  }

  return (
    <ClientStyleContext.Provider value={{ reset }}>
      <CacheProvider value={cache}>{children}</CacheProvider>
    </ClientStyleContext.Provider>
  );
}

const instance = createInstance().use(initReactI18next).use(LanguageDetector);

instance
  .init({
    ...i18nConfig,
    ns: getInitialNamespaces(),
    detection: {
      order: ["cookie", "htmlTag"],
      caches: ["cookie"],
      lookupCookie: "i18next",
      cookieOptions: {
        sameSite: "lax",
      },
    },
  })
  .then(() => hydrate(instance));

function hydrate(instance: i18n) {
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <ClientCacheProvider>
          <I18nextProvider i18n={instance}>
            <RemixBrowser />
            {process.env.NODE_ENV === "production" && <PosthogInit />}
          </I18nextProvider>
        </ClientCacheProvider>
      </StrictMode>
    );
  });
}
